import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

export interface DropdownOption {
  value: string;
  label: string;
  dropdown_label?: string;
  options?: DropdownOption[];
}

export interface QueryMetadata {
  filters: DropdownOption[];
  localities: DropdownOption[];
  columnMappings: Record<string, Record<string, string>>;
}

interface QueryMetadataContextValue {
  data: QueryMetadata;
  loading: boolean;
  error?: string;
  refresh: () => Promise<void>;
}

const QueryMetadataContext = createContext<QueryMetadataContextValue | undefined>(undefined);

export const QueryMetadataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<QueryMetadata>({ filters: [], localities: [], columnMappings: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);
  const initializedRef = useRef(false);

  const fetchMetadata = async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    setError(undefined);
    try {
      const { data: payload } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/query-options`, {
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      setData({
        filters: payload.filters ?? [],
        localities: payload.localities ?? [],
        columnMappings: payload.column_mappings ?? {}
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load filters');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    fetchMetadata();
    return () => abortControllerRef.current?.abort();
  }, []);

  return (
    <QueryMetadataContext.Provider value={{ data, loading, error, refresh: fetchMetadata }}>
      {children}
    </QueryMetadataContext.Provider>
  );
};

export const useQueryMetadataContext = () => {
  const ctx = useContext(QueryMetadataContext);
  if (ctx == null) {
    throw new Error('useQueryMetadataContext must be used within QueryMetadataProvider');
  }
  return ctx;
};
